import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch, getState } from "../../state/store";
import { setCertificateUrl } from "../../state/ducks";
import { selectSelectedTableRowsIds } from "../../state/selectors";

import { certificateAction, strCode, certificateSteps } from "../../consts";
import { t } from "../../services";

import {
  Caption,
  FlexContainers,
  Heading4,
  Modal,
  Paragraph2,
  SmallButton,
  WrapperContainer
} from "../../components";

import ChooseActionStep from "./steps/ChooseActionStep/ChooseActionStep";
import SelectDevicesStep from "./steps/SelectDevicesStep/SelectDevicesStep";
import DownloadingStep from "./steps/DownloadingStep/DownloadingStep";
import RevokeConfirmationStep from "./steps/RevokeConfirmationStep/RevokeConfirmationStep";
import { ActionOptionWrapper } from "./steps/styled";
import { CertificatesModalWrapper } from "./styled";

const CertificatesModal = ({
  mercuryDevices,
  certificatesInfo,
  certificateUrl,
  active,
  loading,
  certificateInfoLoading,
  error,
  setActive,
  onFetchCertificatesInfo,
  onGenerateCertificates,
  onDownloadCertificates,
  onCertificatesRevoke
}) => {
  const [activeStep, setActiveStep] = useState(certificateSteps.CHOOSE_ACTION);
  const [selectedCertificateAction, setSelectedCertificateAction] = useState(
    certificateAction.GENERATE
  );
  const [selectedDevices, setSelectedDevices] = useState([]);

  const [shouldGenerateCerts, setShouldGenerateCerts] = useState(false);

  const selectedTableRowsIds = useSelector((state) => state.global.selectedTableRowsIds);

  const nextButtonText = useMemo(() => {
    if (
      activeStep === certificateSteps.SELECT_DEVICES &&
      selectedCertificateAction === certificateAction.GENERATE
    ) {
      return t(strCode.GENERATE_BTN);
    }

    if (
      activeStep === certificateSteps.SELECT_DEVICES &&
      selectedCertificateAction === certificateAction.DOWNLOAD
    ) {
      return t(strCode.DOWNLOAD_BTN);
    }

    if (
      (activeStep === certificateSteps.SELECT_DEVICES &&
        selectedCertificateAction === certificateAction.REVOKE) ||
      activeStep === certificateSteps.REVOKE_CONFIRMATION
    ) {
      return t(strCode.REVOKE_BTN);
    }

    if (activeStep === certificateSteps.REGENERATE_WARNING) {
      return t(strCode.YES_BTN);
    }

    return t(strCode.NEXT_BTN);
  }, [activeStep, selectedCertificateAction]);

  const prevButtonText = useMemo(() => {
    if (activeStep === certificateSteps.REGENERATE_WARNING) {
      return t(strCode.NO_SKIP_THOSE_BTN);
    }

    if (activeStep === certificateSteps.REVOKE_CONFIRMATION) {
      return t(strCode.CANCEL_BTN);
    }

    return t(strCode.PREVIOUS_BTN);
  }, [activeStep]);

  const modalTitle = useMemo(() => {
    if (activeStep === certificateSteps.REGENERATE_WARNING) {
      return t(strCode.GENERATE_CERTIFICATES_TITLE);
    }

    if (activeStep === certificateSteps.REVOKE_CONFIRMATION) {
      return t(strCode.REVOKE_CERTIFICATES_TITLE);
    }

    if (activeStep !== certificateSteps.CHOOSE_ACTION) {
      if (selectedCertificateAction === certificateAction.GENERATE) {
        return t(strCode.GENERATE_CERTIFICATES);
      }

      if (selectedCertificateAction === certificateAction.DOWNLOAD) {
        return t(strCode.DOWNLOAD_CERTIFICATES);
      }

      if (selectedCertificateAction === certificateAction.REVOKE) {
        return t(strCode.REVOKE_CERTIFICATES);
      }
    }

    return t(strCode.CERTIFICATES_TITTLE);
  }, [activeStep, selectedCertificateAction]);

  const existingCertificateDusns = useMemo(() => {
    if (certificatesInfo) {
      return certificatesInfo
        ?.map((item) => item?.dusn)
        ?.filter((item) => selectedDevices?.includes(item));
    }

    return null;
  }, [certificatesInfo, selectedDevices]);

  const existingCertificateDevices = useMemo(() => {
    if (certificatesInfo && !certificateInfoLoading) {
      return certificatesInfo
        ?.map((info) => {
          const device = mercuryDevices?.find((device) => device?.dusn === info?.dusn);
          if (device) {
            return { ...info, ...device };
          }
        })
        ?.filter((item) => item);
    }

    return [];
  }, [mercuryDevices, certificatesInfo, certificateInfoLoading]);

  const certificateActionOptions = [
    {
      id: certificateAction.GENERATE,
      Component: (
        <ActionOptionWrapper isSelected={selectedCertificateAction === certificateAction.GENERATE}>
          <Paragraph2>{t(strCode.GENERATE_BTN)}</Paragraph2>
          <Caption>{t(strCode.GENERATE_DESCRIPTION)}</Caption>
        </ActionOptionWrapper>
      )
    },
    {
      id: certificateAction.DOWNLOAD,
      Component: (
        <ActionOptionWrapper isSelected={selectedCertificateAction === certificateAction.DOWNLOAD}>
          <Paragraph2>{t(strCode.DOWNLOAD_BTN)}</Paragraph2>
          <Caption>{t(strCode.DOWNLOAD_DESCRIPTION)}</Caption>
        </ActionOptionWrapper>
      )
    },
    {
      id: certificateAction.REVOKE,
      Component: (
        <ActionOptionWrapper isSelected={selectedCertificateAction === certificateAction.REVOKE}>
          <Paragraph2>{t(strCode.REVOKE_BTN)}</Paragraph2>
          <Caption>{t(strCode.REVOKE_DESCRIPTION)}</Caption>
        </ActionOptionWrapper>
      )
    }
  ];

  useEffect(() => {
    return () => {
      setShouldGenerateCerts(false);
      dispatch(setCertificateUrl(null));
    };
  }, []);

  useEffect(() => {
    if (active) {
      setActiveStep(certificateSteps.CHOOSE_ACTION);
      setSelectedCertificateAction(certificateAction.GENERATE);
      setSelectedDevices([]);
    } else {
      dispatch(setCertificateUrl(null));
      setShouldGenerateCerts(false);
    }
  }, [active]);

  useEffect(() => {
    if (!certificateInfoLoading && shouldGenerateCerts && active) {
      onGenerate();
    }
  }, [certificateInfoLoading, shouldGenerateCerts, existingCertificateDusns, active]);

  useEffect(() => {
    if (!loading && (certificateUrl || error) && !shouldGenerateCerts && active) {
      if (certificateUrl) {
        onDownload();
      }

      setActive(false);
    }
  }, [loading, certificateUrl, error, shouldGenerateCerts, active]);

  useEffect(() => {
    if (
      activeStep === certificateSteps.SELECT_DEVICES &&
      selectedCertificateAction === certificateAction.DOWNLOAD
    ) {
      onFetchCertificatesInfo();
    }
  }, [activeStep, selectedCertificateAction]);

  const saveSelectedDevices = () => {
    const selectedDeviceDusns = selectSelectedTableRowsIds()(getState());
    setSelectedDevices(selectedDeviceDusns);

    return selectedDeviceDusns;
  };

  const onActionSelect = (id) => {
    setSelectedCertificateAction(id);
  };

  const switchNextStep = () => {
    switch (activeStep) {
      case certificateSteps.CHOOSE_ACTION:
        setActiveStep(certificateSteps.SELECT_DEVICES);
        break;

      default:
        return;
    }
  };

  const switchPrevStep = () => {
    switch (activeStep) {
      case certificateSteps.SELECT_DEVICES:
        setActiveStep(certificateSteps.CHOOSE_ACTION);
        break;

      default:
        return;
    }
  };

  const onNextClick = () => {
    activeStepConfig.nextStepClickHandler();
  };

  const onPrevClick = () => {
    activeStepConfig.prevStepClickHandler?.();
  };

  const onGenerateNextClick = () => {
    saveSelectedDevices();

    onFetchCertificatesInfo();
    setShouldGenerateCerts(true);
  };

  const onRevokeNextClick = () => {
    saveSelectedDevices();
    setActiveStep(certificateSteps.REVOKE_CONFIRMATION);
  };

  const onDownload = () => {
    onDownloadCertificates(selectedDevices, certificateUrl);
    setActive(false);
  };

  const onGenerate = (override = false, changeActiveStep = true, dusns = selectedDevices) => {
    if (changeActiveStep) {
      setActiveStep(certificateSteps.DOWNLOADING);
    }
    setShouldGenerateCerts(false);

    onGenerateCertificates(dusns, { override });
  };

  const onRevokeConfirmation = () => {
    onCertificatesRevoke(selectedDevices);
    setActive(false);
  };

  const onRevokeCancel = () => {
    setSelectedDevices([]);
    setActiveStep(certificateSteps.CHOOSE_ACTION);
  };

  const onDeviceSelectNextClick = () => {
    switch (selectedCertificateAction) {
      case certificateAction.GENERATE:
        onGenerateNextClick();
        break;

      case certificateAction.DOWNLOAD: {
        const selectedDevices = saveSelectedDevices();
        onGenerate(false, false, selectedDevices);
        break;
      }

      case certificateAction.REVOKE:
        onRevokeNextClick();
        break;

      default:
        break;
    }
  };

  const stepConfigs = [
    {
      step: certificateSteps.CHOOSE_ACTION,
      Component: (
        <ChooseActionStep
          selectedOptionId={selectedCertificateAction}
          actionOptions={certificateActionOptions}
          onOptionSelect={onActionSelect}
        />
      ),
      nextStepClickHandler: switchNextStep
    },
    {
      step: certificateSteps.SELECT_DEVICES,
      Component: (
        <SelectDevicesStep
          deviceData={
            selectedCertificateAction === certificateAction.DOWNLOAD &&
            existingCertificateDevices?.length
              ? existingCertificateDevices
              : mercuryDevices
          }
          isGenerating={selectedCertificateAction === certificateAction.GENERATE}
          loading={
            selectedCertificateAction === certificateAction.DOWNLOAD && certificateInfoLoading
          }
        />
      ),
      nextStepClickHandler: onDeviceSelectNextClick,
      prevStepClickHandler: switchPrevStep
    },
    {
      step: certificateSteps.REVOKE_CONFIRMATION,
      Component: <RevokeConfirmationStep />,
      nextStepClickHandler: onRevokeConfirmation,
      prevStepClickHandler: onRevokeCancel
    },
    {
      step: certificateSteps.DOWNLOADING,
      Component: <DownloadingStep />
    }
  ];

  const activeStepConfig = useMemo(() => {
    return stepConfigs.find((config) => config.step === activeStep);
  }, [activeStep, selectedCertificateAction, certificateInfoLoading, existingCertificateDevices]);

  return (
    <CertificatesModalWrapper
      isGenerating={selectedCertificateAction === certificateAction.GENERATE}>
      <Modal
        withContentVerticalBorders={[
          certificateSteps.CHOOSE_ACTION,
          certificateSteps.SELECT_DEVICES
        ].includes(activeStep)}
        active={active}
        setActive={setActive}
        modalHeader={
          activeStep !== certificateSteps.DOWNLOADING && (
            <WrapperContainer mb={8}>
              <Heading4>{modalTitle}</Heading4>
            </WrapperContainer>
          )
        }
        closable={activeStep !== certificateSteps.DOWNLOADING && !certificateInfoLoading}
        modalFooter={
          activeStep !== certificateSteps.DOWNLOADING && (
            <WrapperContainer Container={FlexContainers.FlexRowEnd} width="100%" gap={8} mt={12}>
              <SmallButton
                onClick={onPrevClick}
                disabled={
                  activeStep === certificateSteps.CHOOSE_ACTION || certificateInfoLoading || loading
                }
                secondary>
                {prevButtonText}
              </SmallButton>

              <SmallButton
                onClick={onNextClick}
                disabled={
                  (activeStep === certificateSteps.SELECT_DEVICES &&
                    !selectedTableRowsIds.length) ||
                  selectedCertificateAction === certificateAction.REVOKE ||
                  certificateInfoLoading ||
                  loading
                }
                loading={certificateInfoLoading || loading}
                backgroundRed={activeStep === certificateSteps.REVOKE_CONFIRMATION}>
                {nextButtonText}
              </SmallButton>
            </WrapperContainer>
          )
        }>
        {activeStepConfig?.Component ? activeStepConfig.Component : null}
      </Modal>
    </CertificatesModalWrapper>
  );
};

CertificatesModal.propTypes = {
  active: PropTypes.bool.isRequired,
  certificatesInfo: PropTypes.arrayOf(
    PropTypes.shape({
      dusn: PropTypes.string.isRequired
    })
  ),
  certificateUrl: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  certificateInfoLoading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  mercuryDevices: PropTypes.arrayOf(
    PropTypes.shape({
      asset_id: PropTypes.string,
      codentify: PropTypes.string,
      deviceImage: PropTypes.string,
      deviceModel: PropTypes.string,
      deviceName: PropTypes.string,
      device_model: PropTypes.string,
      dusn: PropTypes.string,
      isMercuryDevice: PropTypes.bool,
      latest_firmware_version: PropTypes.string,
      latest_firmware_version_update_datetime: PropTypes.string,
      material_brand_family: PropTypes.string,
      material_color: PropTypes.string,
      material_group: PropTypes.string,
      material_model_number: PropTypes.string,
      type: PropTypes.string
    })
  ).isRequired,
  onFetchCertificatesInfo: PropTypes.func.isRequired,
  onGenerateCertificates: PropTypes.func.isRequired,
  onDownloadCertificates: PropTypes.func.isRequired,
  onCertificatesRevoke: PropTypes.func.isRequired
};

export default CertificatesModal;
